import {
  clearOrderIdempotencyKey,
  getOrderIdempotencyKey,
  randomIdempotencyKey,
} from './orderIdempotency';

const REGISTRATION_STORAGE_PREFIX = 'oi.lifecycle-idempotency.v2';
const RANDOM_IDEMPOTENCY_KEY = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const registrationMemoryKeys = new Map();

function normalizedRegistrationEmail(email) {
  const normalized = String(email || '').trim().toLowerCase();
  if (!normalized) throw new Error('Registration idempotency identity is required');
  return normalized;
}

function registrationStorageKey(routeKind, email) {
  if (!['vip_registration', 'guest_registration'].includes(routeKind)) {
    throw new Error('Registration route kind is invalid');
  }
  return `${REGISTRATION_STORAGE_PREFIX}:${routeKind}:${email}`;
}

export function lifecycleIdempotencyKey({ routeKind, actorId, payload, storage }) {
  return getOrderIdempotencyKey({ routeKind, actorId, payload, storage });
}

export function registrationIdempotencyKey({
  email, routeKind = 'vip_registration', storage = sessionStorage,
}) {
  const normalizedEmail = normalizedRegistrationEmail(email);
  const storageKey = registrationStorageKey(routeKind, normalizedEmail);
  const memoryKey = registrationMemoryKeys.get(storageKey);
  if (RANDOM_IDEMPOTENCY_KEY.test(memoryKey || '')) return memoryKey;
  let storedKey = null;
  try { storedKey = storage.getItem(storageKey); } catch { /* use process memory */ }
  if (RANDOM_IDEMPOTENCY_KEY.test(storedKey || '')) {
    registrationMemoryKeys.set(storageKey, storedKey);
    return storedKey;
  }
  const idempotencyKey = randomIdempotencyKey();
  registrationMemoryKeys.set(storageKey, idempotencyKey);
  try { storage.setItem(storageKey, idempotencyKey); } catch { /* process memory protects retries */ }
  return idempotencyKey;
}

export function clearLifecycleIdempotencyKey({ routeKind, actorId, storage }) {
  if (['vip_registration', 'guest_registration'].includes(routeKind)) {
    const normalizedEmail = normalizedRegistrationEmail(actorId);
    const key = registrationStorageKey(routeKind, normalizedEmail);
    registrationMemoryKeys.delete(key);
    try { (storage || sessionStorage).removeItem(key); } catch { /* memory was cleared */ }
    return;
  }
  clearOrderIdempotencyKey({ routeKind, actorId, storage });
}

export function shouldClearLifecycleIdempotencyKey(error) {
  return error?.response?.status === 409;
}

export function requireExactRegistrationResponse(data, email, expectedStatus = 'pending') {
  if (
    !data || typeof data !== 'object'
    || Object.prototype.hasOwnProperty.call(data, 'token')
    || typeof data.message !== 'string' || !data.message
    || typeof data.user?.id !== 'string' || !data.user.id
    || String(data.user.email || '').toLowerCase() !== String(email || '').trim().toLowerCase()
    || !(Array.isArray(expectedStatus) ? expectedStatus : [expectedStatus]).includes(data.user.status)
  ) {
    throw new Error('Registration response could not be verified');
  }
  return data;
}

export function requireExactGuestUpgradeResponse(data, actorId) {
  if (
    !data || typeof data !== 'object'
    || typeof data.message !== 'string' || !data.message
    || data.user?.id !== actorId
    || !['pending', 'approved'].includes(data.user?.status)
  ) {
    throw new Error('Guest upgrade response could not be verified');
  }
  return data;
}

export function requireExactAdminLifecycleResponse(data, expectedMessage) {
  if (
    !data || typeof data !== 'object'
    || data.success !== true
    || data.message !== expectedMessage
  ) {
    throw new Error('Customer lifecycle response could not be verified');
  }
  return data;
}
